import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  AlertProps,
  Box,
  Button,
  Checkbox,
  KeyValuePairs,
  FormField,
  Input,
  Select,
  SpaceBetween,
  Spinner,
  Tiles,
} from "@amzn/awsui-components-react";
import {
  OmsExportPreset,
  OmsProduct,
  OmsReport,
  Stage,
} from "../../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import {
  OmsContextIdentityContext,
  useOmsIdentity,
} from "../../hooks/omsContextIdentity";
import { useOmsContext, useOmsContexts } from "../../hooks/useOmsContexts";
import useOmsReport from "../../hooks/useOmsReport";
import useOmsPresets, {
  presetErrorMessage,
  useOmsExportCapabilities,
} from "../../hooks/useOmsPresets";
import useOmsFilterCriteria, {
  OmsFilterDraft,
} from "../../hooks/useOmsFilterCriteria";
import {
  clearOmsDraft,
  pendingOmsMutation,
  readOmsDraft,
  readOmsRecoveryDraft,
  writeOmsDraft,
} from "../../hooks/omsDraftStorage";
import { PresetRetryExpiredError } from "../../hooks/omsPresetRetry";
import OMSColumns, {
  columnSelectionError,
  OmsColumnsDraft,
} from "./OMSColumns";
import OMSRecordFilters from "./OMSRecordFilters";
import OMSWorkflow, {
  OMSSettingsSection,
  OMSWorkflowSection,
} from "./OMSWorkflow";
import { OMSPresetContents } from "./OMSPresetChanges";
import { OMSHelpLink } from "./OMSContextHelp";
import { OmsConfiguration } from "./omsPresetEditing";
import { presetNameError } from "./omsConfigurationValidation";
import { reportTimestamp } from "./OMSReportSchedule";
import { comparisonLabel } from "./omsReportFormatting";
import {
  contextFiltersToReview,
  omsPresetCopyProducts,
  presetCopyBody,
  presetCopyIssues,
  presetCopyNamespace,
  presetCopySettings,
} from "./omsPresetCopy";

type Destination = { product: OmsProduct; account: string; gl: number };
type CopySource = {
  product: OmsProduct;
  account?: string;
  gl: number;
  label: string;
  namespace: string;
};

function CopyContext({
  source,
  presetName,
  children,
}: {
  source: CopySource;
  presetName: string;
  children?: React.ReactNode;
}) {
  return (
    <OMSWorkflowSection title="Source and destination">
      <KeyValuePairs
        columns={2}
        items={[
          { label: "Original preset", value: presetName },
          {
            label: "From",
            value: `${source.label} (GL ${source.gl})${
              source.account ? `, account ${source.account}` : ", legacy report"
            }`,
          },
        ]}
      />
      {children}
    </OMSWorkflowSection>
  );
}
function copyParent(source: CopySource, onFollow: () => void) {
  return {
    text: "Presets",
    href: `/dashboard/oms/${source.product}${
      source.account ? `/account/${source.account}` : ""
    }/presets`,
    onFollow,
  };
}

function CopyEditor({
  preset,
  report,
  source,
  destination,
  recovery,
  storageNamespace,
  available,
  onBack,
  onDirty,
  onPending,
  onOpenDestination,
  destinationControl,
}: {
  preset: OmsExportPreset;
  report: OmsReport;
  source: CopySource;
  destination: Destination;
  recovery?: OmsFilterDraft;
  storageNamespace: string;
  available: boolean;
  onBack: () => void;
  onDirty: (value: boolean) => void;
  onPending: (value: boolean) => void;
  onOpenDestination: () => void;
  destinationControl: React.ReactNode;
}) {
  const initial = useMemo(
    () => presetCopySettings(preset, report),
    [preset, report]
  );
  const [name, setName] = useState(recovery?.presetEditor?.name || preset.name);
  const recoveredMode = recovery?.tab === "filtered" ? "filtered" : "full";
  const [mode, setMode] = useState<"full" | "filtered">(
    recovery ? recoveredMode : initial.mode
  );
  const criteria = useOmsFilterCriteria(report, undefined, {
    snapshotId: report.snapshotId,
    criteria: recovery?.criteria || initial.criteria,
    operation: recovery?.operation || initial.operation,
    tab: mode,
  });
  const [columns, setColumns] = useState<OmsColumnsDraft>(
    recovery?.columns || initial.columns
  );
  const [workspace, setWorkspace] = useState(
    preset.scope === "columns" ? "columns" : "rows"
  );
  const [reviewing, setReviewing] = useState(Boolean(recovery?.pendingPreset));
  const [edited, setEdited] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [acknowledged, setAcknowledged] = useState("");
  const [saved, setSaved] = useState<OmsExportPreset>();
  const [submitError, setSubmitError] = useState("");
  const errorRef = useRef<AlertProps.Ref>(null);
  const capability = useOmsExportCapabilities();
  const presets = useOmsPresets(
    destination.product,
    Boolean(capability.data?.presets),
    recovery?.presetRequest
  );
  const uncertain =
    pendingOmsMutation(presets.change) ||
    Boolean(
      recovery?.pendingPreset &&
        presets.change.isIdle &&
        presets.changeIdentity()
    );
  const locked = uncertain || presets.change.isLoading;
  const draft: OmsConfiguration = {
    mode,
    criteria: criteria.criteria,
    operation: criteria.operation,
    columns,
  };
  const contextFilters =
    preset.scope === "columns"
      ? []
      : contextFiltersToReview(draft, source, destination);
  const contextSignature = JSON.stringify(contextFilters);
  const needsContextReview =
    contextFilters.length > 0 && acknowledged !== contextSignature;
  const issues = presetCopyIssues(preset, name, draft, report);
  const frozenDraft: OmsFilterDraft = {
    snapshotId: report.snapshotId,
    report,
    editing: true,
    pendingPreset: uncertain,
    criteria: criteria.criteria,
    operation: criteria.operation,
    tab: mode,
    columns,
    appliedPreset: preset,
    presetEditor: { view: "save", name, scope: preset.scope },
    presetRequest: presets.changeIdentity(),
  };
  const serialized = JSON.stringify(frozenDraft);
  const pendingRef = useRef(uncertain);
  pendingRef.current = uncertain;
  useEffect(
    () => () => {
      if (!pendingRef.current)
        clearOmsDraft(destination.product, storageNamespace);
    },
    [destination.product, storageNamespace]
  );
  useEffect(() => {
    if (saved) clearOmsDraft(destination.product, storageNamespace);
    else
      writeOmsDraft(
        destination.product,
        JSON.parse(serialized) as OmsFilterDraft,
        storageNamespace
      );
  }, [serialized, saved, destination.product, storageNamespace]);
  useEffect(() => {
    onDirty(!saved && edited && !locked);
    onPending(!saved && locked);
  }, [edited, locked, saved, onDirty, onPending]);
  useEffect(() => {
    if (presets.change.isError || submitError) errorRef.current?.focus();
  }, [presets.change.error, presets.change.isError, submitError]);

  function change() {
    setEdited(true);
    setSubmitError("");
    if (presets.change.isError && !uncertain) presets.change.reset();
  }
  const editableCriteria = {
    ...criteria,
    setColumn: (...args: Parameters<typeof criteria.setColumn>) => {
      change();
      criteria.setColumn(...args);
    },
    setOperator: (...args: Parameters<typeof criteria.setOperator>) => {
      change();
      criteria.setOperator(...args);
    },
    update: (...args: Parameters<typeof criteria.update>) => {
      change();
      criteria.update(...args);
    },
    setOperation: (...args: Parameters<typeof criteria.setOperation>) => {
      change();
      criteria.setOperation(...args);
    },
    add: () => {
      change();
      criteria.add();
    },
    remove: (index: number) => {
      change();
      criteria.remove(index);
    },
    clear: () => {
      change();
      criteria.clear();
    },
  };
  function review() {
    setAttempted(true);
    if (issues.length || needsContextReview) {
      setSubmitError(
        issues[0] || "Review the account and GL filters before continuing."
      );
      if (
        needsContextReview ||
        (preset.scope !== "columns" &&
          mode === "filtered" &&
          (!criteria.valid || criteria.criteria.length > 20))
      )
        setWorkspace("rows");
      else if (
        preset.scope !== "filters" &&
        columnSelectionError(columns, report)
      )
        setWorkspace("columns");
      return;
    }
    setSubmitError("");
    setReviewing(true);
  }
  async function save() {
    if ((!available || !capability.data?.presets) && !locked) return;
    if (!locked && (issues.length || needsContextReview)) {
      setReviewing(false);
      review();
      return;
    }
    try {
      const result = await presets.change.mutateAsync({
        action: "save",
        body: presetCopyBody(preset, name, draft, report),
      });
      if (result.preset) {
        clearOmsDraft(destination.product, storageNamespace);
        setSaved(result.preset);
        setEdited(false);
      }
    } catch {
      // The shared mutation retains its request identity and contextual error.
    }
  }
  const targetProduct = omsPresetCopyProducts.find(
    (item) => item.value === destination.product
  );
  const destinationLabel = `${
    targetProduct?.label || destination.product
  } (GL ${destination.gl}), account ${destination.account}`;
  const saveLabel = locked ? "Retry save" : "Save preset";
  if (saved)
    return (
      <OMSWorkflow
        heading="Preset copied"
        stage="result"
        completionLabel="Saved"
        focusHeading
        parent={copyParent(source, onBack)}
        footer={
          <div className="oms-preset-copy__footer">
            <Button onClick={onBack}>Back to original presets</Button>
            <Button variant="primary" onClick={onOpenDestination}>
              Open destination presets
            </Button>
          </div>
        }
      >
        <OMSWorkflowSection title="Saved preset">
          <Alert type="success">
            {saved.name} is saved for {destinationLabel}.
          </Alert>
        </OMSWorkflowSection>
      </OMSWorkflow>
    );
  return (
    <OMSWorkflow
      heading={reviewing ? "Review preset" : "Reuse preset"}
      headingInfo={
        <OMSHelpLink topic="copy-presets" label="About reusing presets" />
      }
      stage={reviewing ? "review" : "settings"}
      completionLabel="Saved"
      focusHeading
      parent={copyParent(source, onBack)}
      footer={
        <div className="oms-preset-copy__footer">
          {reviewing ? (
            <Button disabled={locked} onClick={() => setReviewing(false)}>
              Back to settings
            </Button>
          ) : (
            <Button onClick={onBack}>Cancel</Button>
          )}
          <Button
            variant="primary"
            loading={presets.change.isLoading}
            disabled={(!available || !capability.data?.presets) && !locked}
            onClick={() => {
              if (reviewing) save().catch(() => undefined);
              else review();
            }}
          >
            {reviewing ? saveLabel : "Review preset"}
          </Button>
        </div>
      }
    >
      <SpaceBetween size="l">
        <CopyContext source={source} presetName={preset.name}>
          {reviewing ? (
            <KeyValuePairs
              columns={2}
              items={[
                { label: "Destination", value: destinationLabel },
                { label: "Preset name", value: name },
              ]}
            />
          ) : (
            <div className="oms-copy-context-fields">
              {destinationControl}
              <FormField
                className="oms-preset-name"
                label="Preset name"
                errorText={
                  attempted || nameTouched ? presetNameError(name) : undefined
                }
              >
                <Input
                  ariaLabel="New preset name"
                  value={name}
                  onBlur={() => setNameTouched(true)}
                  onChange={({ detail }) => {
                    change();
                    setName(detail.value);
                  }}
                />
              </FormField>
            </div>
          )}
          {reviewing && (
            <Box color="text-body-secondary">
              A new private preset will be saved for this destination.
            </Box>
          )}
          {reviewing && (
            <Box color="text-body-secondary">
              Report refreshed: {reportTimestamp(report.sourceRefreshedAt)}
            </Box>
          )}
        </CopyContext>
        {(presets.change.isError || submitError) && (
          <Alert
            ref={errorRef}
            type="error"
            header={
              submitError ? "Check preset settings" : "Could not save preset"
            }
            action={
              presets.change.error instanceof PresetRetryExpiredError &&
              presets.change.error.reconciled ? (
                <Button
                  onClick={() => {
                    presets.restartExpiredRequest();
                    setReviewing(false);
                  }}
                >
                  Start new save
                </Button>
              ) : undefined
            }
          >
            {submitError || presetErrorMessage(presets.change.error)}
          </Alert>
        )}
        {(!available || !capability.data?.presets) && (
          <Alert type="warning" header="Saving presets unavailable">
            Your preset settings are preserved.
            <Button
              onClick={() => {
                capability.refetch().catch(() => undefined);
              }}
            >
              Check again
            </Button>
          </Alert>
        )}
        {recovery?.pendingPreset && presets.change.isIdle && (
          <Alert type="info" header="Recover the earlier save">
            Retry to check the original request. Its settings and destination
            are preserved.
          </Alert>
        )}
        {reviewing ? (
          <OMSWorkflowSection title="Preset settings">
            <OMSPresetContents
              draft={draft}
              scope={preset.scope}
              report={report}
              collapsible={false}
            />
          </OMSWorkflowSection>
        ) : (
          <OMSSettingsSection
            title="Preset settings"
            notice={
              contextFilters.length > 0 && (
                <Alert type="warning" header="Check account and GL filters">
                  <SpaceBetween size="s">
                    <Box>
                      These filters depend on the GL or account. Review their
                      conditions and values for {destinationLabel}.
                    </Box>
                    <Box>
                      {contextFilters.slice(0, 3).map((filter) => (
                        <div key={filter.id}>
                          {filter.column}{" "}
                          {comparisonLabel(
                            filter.operator,
                            report.columnTypes?.[filter.column]
                          )}{" "}
                          {(filter.values || [filter.value || '""'])
                            .slice(0, 3)
                            .join(", ")}
                        </div>
                      ))}
                      {contextFilters.length > 3 &&
                        `${
                          contextFilters.length - 3
                        } more filters are shown in Rows.`}
                    </Box>
                    <Checkbox
                      checked={acknowledged === contextSignature}
                      onChange={({ detail }) =>
                        setAcknowledged(detail.checked ? contextSignature : "")
                      }
                    >
                      I have reviewed the account and GL filters.
                    </Checkbox>
                  </SpaceBetween>
                </Alert>
              )
            }
            ariaLabel="Copied preset settings"
            activeTabId={workspace}
            onChange={({ detail }) => setWorkspace(detail.activeTabId)}
            tabs={[
              ...(preset.scope !== "columns"
                ? [
                    {
                      id: "rows",
                      label: "Rows",
                      content: (
                        <SpaceBetween size="l">
                          <FormField label="Rows to export">
                            <Tiles
                              value={mode}
                              items={[
                                { value: "full", label: "All rows" },
                                { value: "filtered", label: "Filtered rows" },
                              ]}
                              onChange={({ detail }) => {
                                change();
                                setMode(detail.value as "full" | "filtered");
                              }}
                            />
                          </FormField>
                          {mode === "filtered" && (
                            <OMSRecordFilters
                              report={report}
                              state={editableCriteria}
                              embedded
                              attempted={attempted}
                            />
                          )}
                        </SpaceBetween>
                      ),
                    },
                  ]
                : []),
              ...(preset.scope !== "filters"
                ? [
                    {
                      id: "columns",
                      label: "Columns",
                      content: (
                        <OMSColumns
                          page
                          report={report}
                          settings={columns}
                          attempted={attempted}
                          onChange={(next) => {
                            change();
                            setColumns(next);
                          }}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </SpaceBetween>
    </OMSWorkflow>
  );
}

function CopyTarget({
  source,
  preset,
  destination,
  recovery,
  storageNamespace,
  ...callbacks
}: {
  source: CopySource;
  preset: OmsExportPreset;
  destination: Destination;
  recovery?: OmsFilterDraft;
  storageNamespace: string;
  available: boolean;
  onBack: () => void;
  onDirty: (value: boolean) => void;
  onPending: (value: boolean) => void;
  onOpenDestination: () => void;
  destinationControl: React.ReactNode;
}) {
  const report = useOmsReport(destination.product, !recovery?.report);
  const matchesDestination = (candidate?: OmsReport) =>
    candidate?.product === destination.product &&
    candidate.financialAccount === destination.account &&
    (candidate.gl === undefined || candidate.gl === destination.gl);
  const [pinned, setPinned] = useState(
    matchesDestination(recovery?.report) ? recovery?.report : undefined
  );
  useEffect(() => {
    if (
      !pinned &&
      report.data?.product === destination.product &&
      report.data.financialAccount === destination.account &&
      (report.data.gl === undefined || report.data.gl === destination.gl) &&
      !report.data.unavailableReason
    )
      setPinned(report.data);
  }, [
    pinned,
    report.data,
    destination.product,
    destination.account,
    destination.gl,
  ]);
  if (recovery?.report && !matchesDestination(recovery.report))
    return (
      <Alert type="error" header="Saved destination is invalid">
        The recovered report does not match this destination.
        <Button onClick={callbacks.onBack}>Back to presets</Button>
      </Alert>
    );
  if (!pinned)
    return (
      <OMSWorkflow
        heading="Reuse preset"
        headingInfo={
          <OMSHelpLink topic="copy-presets" label="About reusing presets" />
        }
        stage="settings"
        completionLabel="Saved"
        parent={copyParent(source, callbacks.onBack)}
        footer={<Button onClick={callbacks.onBack}>Cancel</Button>}
      >
        <CopyContext source={source} presetName={preset.name}>
          {callbacks.destinationControl}
        </CopyContext>
        {report.isLoading ? (
          <Spinner />
        ) : (
          <Alert type="warning" header="Destination report unavailable">
            No verified report is available for this destination.
            <Button
              onClick={() => {
                report.refetch().catch(() => undefined);
              }}
            >
              Check again
            </Button>
          </Alert>
        )}
      </OMSWorkflow>
    );
  return (
    <CopyEditor
      preset={recovery?.appliedPreset || preset}
      report={pinned}
      source={source}
      destination={destination}
      recovery={recovery}
      storageNamespace={storageNamespace}
      {...callbacks}
    />
  );
}

export default function OMSPresetCopy({
  product,
  account,
  presetId,
  destinationKey,
  onDestination,
  onBack,
  onDirty,
  onOpenDestination,
}: {
  product: OmsProduct;
  account?: string;
  presetId: string;
  destinationKey?: string;
  onDestination: (key: string) => void;
  onBack: () => void;
  onDirty: (value: boolean) => void;
  onOpenDestination: (product: OmsProduct, account: string) => void;
}) {
  const identity = useOmsIdentity(product);
  const discovered = useOmsContexts();
  const [pending, setPending] = useState(false);
  const [pinnedPreset, setPinnedPreset] = useState<OmsExportPreset>();
  useEffect(() => {
    // Copying has its own editor; the original export draft stays in its context.
    onDirty(false);
  }, [onDirty]);
  const pendingRecovery = useMemo(() => {
    if (!/^(channels|addon|tvod):\d{5}$/.test(destinationKey || ""))
      return undefined;
    const [targetProduct, targetAccount] = (destinationKey || "").split(":");
    const knownProduct = omsPresetCopyProducts.find(
      (item) => item.value === targetProduct
    );
    if (!knownProduct || !/^\d{5}$/.test(targetAccount || "")) return undefined;
    const candidates = [`${targetProduct}:account:${targetAccount}`];
    if (targetAccount === "13051") candidates.push(targetProduct);
    return candidates
      .map((namespace) => {
        const storageNamespace = presetCopyNamespace(
          identity.namespace,
          presetId,
          namespace
        );
        const stored = (pending ? readOmsDraft : readOmsRecoveryDraft)(
          knownProduct.value,
          storageNamespace
        );
        if (
          stored?.pendingPreset &&
          stored.presetRequest &&
          stored.appliedPreset?.presetId === presetId &&
          stored.report?.financialAccount === targetAccount
        )
          return {
            context: {
              product: knownProduct.value,
              financialAccount: targetAccount,
              gl: knownProduct.gl,
            },
            storageNamespace,
            draft: stored,
          };
        return undefined;
      })
      .find((candidate) => Boolean(candidate));
  }, [destinationKey, identity.namespace, presetId, pending]);
  const selectedContext =
    discovered.data?.contexts.find(
      (item) => `${item.product}:${item.financialAccount}` === destinationKey
    ) || pendingRecovery?.context;
  const rememberedContext = useRef<{
    key?: string;
    context: NonNullable<typeof selectedContext>;
  }>();
  if (selectedContext)
    rememberedContext.current = {
      key: destinationKey,
      context: selectedContext,
    };
  const selected =
    selectedContext ||
    (rememberedContext.current?.key === destinationKey
      ? rememberedContext.current?.context
      : undefined);
  const detail = useOmsContext(selected?.product, selected?.financialAccount);
  const sourceQuery = useQuery({
    queryKey: ["oms-preset-copy-source", identity.namespace, presetId],
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const preset = (
        await OmsReportsApiFactory().getOmsExportPreset(
          product,
          Stage.Prod,
          presetId,
          account,
          { timeout: 15_000 }
        )
      ).data;
      if (preset.formatVersion !== 1)
        throw new Error("Unsupported preset format");
      return preset;
    },
  });
  useEffect(() => {
    if (sourceQuery.isFetchedAfterMount && sourceQuery.data && !pinnedPreset)
      setPinnedPreset(sourceQuery.data);
  }, [sourceQuery.isFetchedAfterMount, sourceQuery.data, pinnedPreset]);
  const copyNamespace = presetCopyNamespace(
    identity.namespace,
    presetId,
    detail.data?.canonicalNamespace || ""
  );
  const recovery = useMemo(
    () =>
      selected && detail.data
        ? readOmsRecoveryDraft(selected.product, copyNamespace)
        : undefined,
    [copyNamespace, selected, detail.data]
  );
  useEffect(() => {
    if (!pinnedPreset && recovery?.appliedPreset)
      setPinnedPreset(recovery.appliedPreset);
  }, [pinnedPreset, recovery?.appliedPreset]);
  const preset = recovery?.appliedPreset || pinnedPreset;
  const sourceProduct = omsPresetCopyProducts.find(
    (item) => item.value === product
  );
  const resolved =
    selected &&
    detail.data?.product === selected.product &&
    detail.data.financialAccount === selected.financialAccount;
  const eligible =
    resolved &&
    detail.data &&
    detail.data.capabilities.newAdmissions &&
    detail.data.capabilities.newExportControls &&
    !detail.data.unavailableReason &&
    detail.data.canonicalNamespace !== identity.namespace;
  const recovering =
    resolved &&
    pendingRecovery &&
    detail.data &&
    pendingRecovery.storageNamespace === copyNamespace &&
    pendingRecovery.draft.report?.product === selected?.product;
  const opened = useRef<{ key?: string; namespace: string }>();
  if (eligible || recovering)
    opened.current = { key: destinationKey, namespace: copyNamespace };
  const active =
    opened.current?.key === destinationKey &&
    opened.current?.namespace === copyNamespace;
  const targetIdentity = useMemo(
    () => ({
      namespace: detail.data?.canonicalNamespace || "",
      financialAccount: selected?.financialAccount,
    }),
    [detail.data?.canonicalNamespace, selected?.financialAccount]
  );
  let destinationStatus: "loading" | "error" | "finished" = "finished";
  if (discovered.isLoading) destinationStatus = "loading";
  else if (discovered.isError) destinationStatus = "error";
  const source: CopySource = {
    product,
    account,
    gl: sourceProduct?.gl || 0,
    label: sourceProduct?.label || product,
    namespace: identity.namespace,
  };
  const destinationControl = (
    <FormField
      className="oms-preset-copy__destination"
      label="Destination report"
    >
      <Select
        expandToViewport
        disabled={pending}
        ariaLabel="Destination GL and financial account"
        filteringType="auto"
        filteringPlaceholder="Find product, GL or account"
        filteringAriaLabel="Find destination GL or account"
        filteringClearAriaLabel="Clear destination search"
        placeholder="Choose a GL and financial account"
        selectedOption={
          selected
            ? {
                value: destinationKey,
                label: `${
                  omsPresetCopyProducts.find(
                    (item) => item.value === selected.product
                  )?.label || selected.product
                } (${selected.financialAccount})`,
              }
            : null
        }
        options={omsPresetCopyProducts.map((item) => ({
          label: `${item.label} (GL ${item.gl})`,
          options: (discovered.data?.contexts || [])
            .filter(
              (context) =>
                context.product === item.value &&
                !(
                  context.product === product &&
                  context.financialAccount === account
                )
            )
            .map((context) => ({
              value: `${context.product}:${context.financialAccount}`,
              label: context.financialAccount,
              filteringTags: [item.label, String(item.gl)],
            })),
        }))}
        statusType={destinationStatus}
        loadingText="Loading destinations"
        errorText="Destinations could not be loaded"
        recoveryText="Retry"
        noMatch="No matching destinations"
        onLoadItems={() => {
          if (discovered.isError) discovered.refetch().catch(() => undefined);
        }}
        onChange={({ detail: change }) =>
          onDestination(change.selectedOption.value || "")
        }
      />
    </FormField>
  );
  if ((eligible || recovering || active) && preset && selected && detail.data)
    return (
      <OmsContextIdentityContext.Provider value={targetIdentity}>
        <CopyTarget
          key={copyNamespace}
          source={source}
          preset={preset}
          destination={{
            product: selected.product,
            account: selected.financialAccount,
            gl: selected.gl,
          }}
          recovery={recovery}
          storageNamespace={copyNamespace}
          available={Boolean(eligible)}
          destinationControl={destinationControl}
          onDirty={onDirty}
          onPending={setPending}
          onBack={onBack}
          onOpenDestination={() =>
            onOpenDestination(selected.product, selected.financialAccount)
          }
        />
      </OmsContextIdentityContext.Provider>
    );
  return (
    <OMSWorkflow
      heading="Reuse preset"
      headingInfo={
        <OMSHelpLink topic="copy-presets" label="About reusing presets" />
      }
      stage="settings"
      completionLabel="Saved"
      parent={copyParent(source, onBack)}
      footer={<Button onClick={onBack}>Cancel</Button>}
    >
      <SpaceBetween size="l">
        <CopyContext
          source={source}
          presetName={
            preset?.name ||
            (sourceQuery.isLoading ? "Loading preset…" : "Unavailable")
          }
        >
          {destinationControl}
        </CopyContext>
        {!preset && sourceQuery.isError && (
          <Alert type="error" header="Could not load the original preset">
            {presetErrorMessage(sourceQuery.error)}
            <Button
              onClick={() => {
                sourceQuery.refetch().catch(() => undefined);
              }}
            >
              Retry
            </Button>
          </Alert>
        )}
        {destinationKey && !selected && !discovered.isLoading && (
          <Alert type="warning">
            Choose an available destination from the list.
          </Alert>
        )}
        {selected && detail.isLoading && <Spinner />}
        {selected &&
          !detail.isLoading &&
          !eligible &&
          !recovering &&
          !active && (
            <Alert type="warning" header="Destination unavailable">
              {detail.data?.canonicalNamespace === identity.namespace
                ? "Choose a different GL or account."
                : detail.data?.unavailableReason ||
                  "This destination is not available for new presets."}
              {detail.isError && (
                <Button
                  onClick={() => {
                    detail.refetch().catch(() => undefined);
                  }}
                >
                  Retry
                </Button>
              )}
            </Alert>
          )}
      </SpaceBetween>
    </OMSWorkflow>
  );
}
